import { put, takeEvery, all, select, call, debounce } from 'redux-saga/effects'
import * as Actions from 'actions/journalists'
import * as AppActions from 'actions/app'
import * as Selectors from 'selectors'
import * as Api from 'api/journalists'
import * as ContactsManagementApi from 'api/contacts_management'

export function* searchJournalists({ type }) {
  const i18n = yield select(Selectors.getI18n)
  try {
    const postBody = yield select(Selectors.getJournalistsSearchBody, type)

    const response = yield call(Api.search, postBody)

    yield put(Actions.searchJournalistsSuccess(response))
  } catch (error) {
    yield put(Actions.searchJournalistsError(error))

    if (error.response && error.response.statusCode === 403) {
      yield put(AppActions.showAppSnackbarMessage({
        text: i18n.get('invalid_search_query_helper_text'),
        variant: 'error'
      }))
    } else {
      yield put(AppActions.showAppSnackbarMessage({
        text: i18n.get('failed'),
        variant: 'error'
      }))
    }
  }
}

export function* simpleSearch() {
  yield put(Actions.searchJournalistsStart({ type: 'search' }))
}

export function* selectAllJournalists() {
  const journalists = yield select(Selectors.getJoDaJournalists)
  const journalistIds = journalists.get('items').map(j => j.get('id'))
  const currentSelectedJournalistIds = yield select(Selectors.getJoUiSelectedJournalistIds)
  const newSelectedJournalistIds = currentSelectedJournalistIds.concat(journalistIds).toSet().toList()

  yield put(Actions.setSelectedJournalistIds(newSelectedJournalistIds))
}

export function* deselectAllJournalists() {
  const journalists = yield select(Selectors.getJoDaJournalists)
  const journalistIds = journalists.get('items').map(j => j.get('id'))
  const currentSelectedJournalistIds = yield select(Selectors.getJoUiSelectedJournalistIds)
  const newSelectedJournalistIds = currentSelectedJournalistIds.filter(id => !journalistIds.includes(id))

  yield put(Actions.setSelectedJournalistIds(newSelectedJournalistIds))
}

export function* addJournalistToList() {
  const i18n = yield select(Selectors.getI18n)

  try {
    const ids = yield select(Selectors.getJoUiDistributionListsDialogJoIds)
    const selectedDistributionLists = yield select(Selectors.getJoUiSelectedDistributionLists)
    const distributionListIds = selectedDistributionLists.map(dl => dl.get('id'))
    const newDistributionLists = yield select(Selectors.getJoUiNewDistributionLists)

    const postBody = {
      ids: ids.toJS(),
      distribution_list_ids: distributionListIds.toJS(),
      new_distribution_lists: newDistributionLists.toJS()
    }

    const response = yield call(Api.importJournalists, postBody)

    yield put(Actions.importJournalistsToListSuccess(response))
    yield put(Actions.closeDistributionListsDialog())
  } catch (error) {
    yield put(Actions.importJournalistsToListError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

export function* addJournalist({ payload: ids }) {
  const i18n = yield select(Selectors.getI18n)

  try {
    const postBody = {
      ids
    }
    const response = yield call(Api.importJournalists, postBody)

    yield put(Actions.importJournalistsSuccess(response))
  } catch (error) {
    yield put(Actions.importJournalistsError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

export function* loadJournalistNews() {
  const i18n = yield select(Selectors.getI18n)

  try {
    const journalistId = yield select(Selectors.getJoUiDetailPageJournalistId)
    const body = {
      size: 20
    }

    const response = yield call(Api.loadJournalistNews, journalistId, body)

    yield put(Actions.loadJournalistNewsSuccess(response.news))
  } catch (error) {
    yield put(Actions.loadJournalistNewsError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

export function* searchDistributionLists({ payload }) {
  const i18n = yield select(Selectors.getI18n)

  try {
    const body = {
      filter: payload.filter,
      page: 1,
      limit: 10,
      sort_by: 'name',
      sort_order: 'asc'
    }

    const response = yield call(ContactsManagementApi.fetchDistributionLists, body)

    yield put(Actions.searchDistributionListsSuccess(response))
  } catch (error) {
    yield put(Actions.searchDistributionListsError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

export function* searchJobTitles({ payload }) {
  const i18n = yield select(Selectors.getI18n)

  try {
    const body = {
      query: payload
    }
    const response = yield call(Api.findJobTitles, body)

    yield put(Actions.searchJobTitlesSuccess(response.jobTitles))
  } catch (error) {
    yield put(Actions.searchJobTitlesError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

export function* fetchPublications({ payload }) {
  const i18n = yield select(Selectors.getI18n)

  try {
    const channelIds = yield select(Selectors.getJoFiChannels)

    let name = payload

    if ([' ', '*'].indexOf(payload.slice(-1) === -1)) {
      name = `${payload}*`
    }

    const postBody = {
      name,
      channel_ids: channelIds,
      size: 50
    }
    const response = yield call(Api.findPublications, postBody)

    yield put(Actions.fetchPublicationsSuccess(response.publications))
  } catch (error) {
    yield put(Actions.fetchPublicationsError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

// taker functions

export function* watchSimpleSearchStart() {
  yield takeEvery(Actions.simpleSearchStart, simpleSearch)
}

export function* watchPageAllContacts() {
  yield takeEvery(Actions.pageAllContacts, searchJournalists, { type: 'allContactsPagination' })
}

export function* watchPageMyContacts() {
  yield takeEvery(Actions.pageMyContacts, searchJournalists, { type: 'myContactsPagination' })
}

export function* watchSearchJournalistsStart() {
  yield takeEvery(Actions.searchJournalistsStart, searchJournalists, { type: 'search' })
}

export function* watchAddJournalistToListStart() {
  yield takeEvery(Actions.importJournalistsToListStart, addJournalistToList)
}

export function* watchAddJournalistStart() {
  yield takeEvery(Actions.importJournalistsStart, addJournalist)
}

export function* watchLoadJournalistNewsStart() {
  yield takeEvery(Actions.loadJournalistNewsStart, loadJournalistNews)
}

export function* watchSearchDistributionListsStart() {
  yield debounce(1000, Actions.searchDistributionListsStart, searchDistributionLists)
}

export function* watchSearchJobTitlesStart() {
  yield debounce(500, Actions.searchJobTitlesStart, searchJobTitles)
}

export function* watchFetchPublicationsStart() {
  yield debounce(500, Actions.fetchPublicationsStart, fetchPublications)
}

export function* watchSelectAllJournalists() {
  yield takeEvery(Actions.selectAllJournalists, selectAllJournalists)
}

export function* watchDeselectAllJournalists() {
  yield takeEvery(Actions.deselectAllJournalists, deselectAllJournalists)
}

export default function* journalistSearchSaga() {
  yield all([
    watchPageAllContacts(),
    watchPageMyContacts(),
    watchSimpleSearchStart(),
    watchSearchJournalistsStart(),
    watchAddJournalistToListStart(),
    watchAddJournalistStart(),
    watchLoadJournalistNewsStart(),
    watchSearchDistributionListsStart(),
    watchSearchJobTitlesStart(),
    watchFetchPublicationsStart(),
    watchSelectAllJournalists(),
    watchDeselectAllJournalists()
  ])
}
